import React, {
    useMemo, useEffect, useState,
} from 'react';
import { useTransition, animated } from '@react-spring/web';

import classNames from 'classnames';
import { SliderDotsWrapper } from './SliderDotsWrapper';
import {
    dotSize, activeDotSize, gapSize, gapSizeV2,
    dotSizeV2,
    activeDotSizeV2,
} from './sliderDots.const';

import css from './sliderDots.module.scss';


type SliderDotsProps = {
    slidesCount: number,
    currentSlideIndex: number,
    additionalClass?: string,
}

type Dot = {
    id: number,
    isActive: boolean,
    width: number,
}

export function SliderDots({
    slidesCount,
    currentSlideIndex,
    additionalClass = '',
}: SliderDotsProps) {
    const isNewVersion = additionalClass === 'v2';
    const dots = useMemo(() => {
        const dotsData = Array.from(Array(slidesCount)).map((_, index) => {
            const currentActiveDotSize = isNewVersion ? activeDotSizeV2 : activeDotSize;
            const currentDotSize = isNewVersion ? dotSizeV2 : dotSize;
            const id = index;
            const isActive = index === currentSlideIndex;
            const dot: Dot = { id, isActive, width: isActive ? currentActiveDotSize : currentDotSize };
            return dot;
        });

        return dotsData;
    }, [slidesCount, currentSlideIndex, isNewVersion]);

    const [dotsState, setDotsState] = useState(dots);


    useEffect(() => {
        const currentIndex = dotsState.findIndex((d) => d.isActive);
        const nextElement = dotsState[currentSlideIndex];

        const nextDots = dotsState.map((dot, index) => {
            if (index === currentSlideIndex) {
                return dotsState[currentIndex];
            }
            if (index === currentIndex) {
                return nextElement;
            }
            return dot;
        });

        setDotsState(nextDots);
    // вычисляем новый state, только когда меняется currentSlideIndex
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSlideIndex]);


    let totalIndent = 0;
    const transitions = useTransition(
        dotsState.map((data, i) => {
            const currentGapSize = isNewVersion ? gapSizeV2 : gapSize;
            const gap = i === 0 ? 0 : currentGapSize;
            totalIndent += data.width + gap;
            const x = totalIndent - data.width;

            return { ...data, x };
        }),
        {
            key: (dot: Dot) => dot.id,
            from: { width: 0, opacity: 0 },
            leave: { width: 0, opacity: 0 },
            /* @ts-ignore */
            enter: ({ x, width }) => ({ x, width, opacity: 1 }),
            /* @ts-ignore */
            update: ({ x, width }) => ({ x, width }),

            // https://react-spring.dev/docs/advanced/config
            config: {
                tension: 400,
            },
        },
    );

    const dotStyles = classNames({
        [css.sliderDot]: true,
        [css[additionalClass]]: Boolean(additionalClass),
    });


    return (
        <SliderDotsWrapper
            additionalClass={additionalClass}
            slidesCount={slidesCount}
            currentSlideIndex={currentSlideIndex}
        >
            {/* @ts-ignore */}
            {transitions((style, item, t, index) => (
                <animated.div
                    className={dotStyles}
                    style={{
                        ...style,
                        zIndex: dotsState.length - index,
                        opacity: currentSlideIndex === index ? 1 : 0.5,
                    }}
                >
                    <div className={dotStyles} />
                </animated.div>
            ))}
        </SliderDotsWrapper>
    );
}
